import { useQuery, useQueries } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-endpoints';
import authService from '../services/authService';

export interface ExamResultData {
  user_id: number;
  exam_id: number;
  score: number;
  total_score: number;
  passed: number;
}

export function useExamQuestions(examId?: string) {
  return useQuery({
    queryKey: ['examQuestions', examId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_EXAM_QUESTIONS(examId || '1')}`;
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      if (res.error) {
        throw new Error('Failed to fetch exam questions');
      }
      console.log('Exam Questions:', res.data);
      return res.data as any[];
    },
    enabled: !!examId,
  });
}

// Function to submit exam result
export const submitExamResult = async (examResultData: ExamResultData) => {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  const url = `${baseURL}${API_ENDPOINTS.CREATE_EXAM_RESULT}`;
  
  console.log('Submitting exam result:', examResultData);
  console.log('Full URL:', url);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authService.getToken()}`,
    },
    body: JSON.stringify(examResultData),
  });

  const result = await response.json();
  
  console.log('Exam Result API Response:', result);
  
  if (!response.ok || result.error) {
    throw new Error(result.message || 'Failed to submit exam result');
  }
  
  return result.data || result;
};

export function useUserExamResultDetails(examId?: string, userId?: string) {
  return useQuery({
    queryKey: ['examResultDetails', examId, userId],
    queryFn: async () => {
      if (!examId || !userId) return null;
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_EXAM_RESULT_DETAILS(examId, userId)}`;
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      if (res.error) {
        throw new Error('Failed to fetch user exam result details');
      }
      console.log('User Exam Result Details:', res.data);
      return res.data as any[];
    },
    enabled: !!examId && !!userId,
  });
}

export interface ExamUserRetake {
  id: number;
  exam_id: number;
  user_id: number;
  is_retake: boolean;
  created_at: string;
}

export function useExamUserRetake(examId?: string, userId?: string) {
  return useQuery({
    queryKey: ['examUserRetake', examId, userId],
    queryFn: async () => {
      if (!examId || !userId) return null;
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_EXAM_USER_RETAKE(examId, userId)}`;
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      if (res.error) {
        console.log('No retake permission found for exam:', examId);
        return null;
      }
      console.log('Exam User Retake:', res.data);
      return res.data as ExamUserRetake;
    },
    enabled: !!examId && !!userId,
  });
}

export function useExamUserRetakes(examIds: string[], userId?: string) {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  
  return useQueries({
    queries: examIds.map((examId) => ({
      queryKey: ['examUserRetake', examId, userId],
      queryFn: async () => {
        if (!examId || !userId) return null;
        const url = `${baseURL}${API_ENDPOINTS.GET_EXAM_USER_RETAKE(examId, userId)}`;
      
        try {
          const res: any = await (await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`,
            },
          })).json();
          
          if (res.error) {
            return null;
          }
          return res.data as ExamUserRetake;
        } catch (error) {
          console.log('Error fetching retake for exam:', examId);
          return null;
        }
      },
      enabled: !!examId && !!userId,
    })),
  });
}